import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ACCORDION_TITLE_CLASS } from "@/lib/accordion-content";
import { Multiplesample } from "@/lib/types";
import { useMemo } from "react";
import { computeFieldCollectorName } from "@/lib/computeFields";
import { SupportedLocale } from "@/i18n";

const DRAFT_STATUS = "draft";
const PUBLISHED_STATUS = "published";

interface ExemplaireWithCollector extends Multiplesample {
  collector?: {
    id: string;
    collectionneursDescriptionNom?: string;
    collectionneursDescriptionPrenom?: string;
    collectionneursDescriptionCollectionPublique?: boolean;
    collectionneursDescriptionVille?: string;
    collectionneursDescriptionVillePublished?: boolean;
    collectionneursDescriptionPays?: string;
    collectionneursDescriptionPaysPublished?: boolean;
  };
}

interface CollectionsPubliquesAccordionProps {
  exemplaires: Multiplesample[];
  locale: SupportedLocale;
  t: (key: string) => string;
  translation: (key: string) => string;
}

export function CollectionsPubliquesAccordion({
  exemplaires,
  locale,
  t,
  translation,
}: CollectionsPubliquesAccordionProps) {
  const publicCollectionsFromExemplaires = useMemo(() => {
    const allowedStatuses = [DRAFT_STATUS, PUBLISHED_STATUS];
    const withPublicCollection = (exemplaires ?? []).filter(
      (e) =>
        allowedStatuses.includes(
          (e as ExemplaireWithCollector).exemplaireDansEstampesMultiplesDescriptionStatus ?? ""
        ) &&
        (e as ExemplaireWithCollector).collector
          ?.collectionneursDescriptionCollectionPublique
    ) as ExemplaireWithCollector[];
    const seenCollectorIds = new Set<string>();
    return withPublicCollection.filter((e) => {
      if (!e.collector?.id) return false;
      if (seenCollectorIds.has(e.collector.id)) return false;
      seenCollectorIds.add(e.collector.id);
      return true;
    });
  }, [exemplaires]);

  if (publicCollectionsFromExemplaires.length === 0) return null;

  return (
    <AccordionItem value="collections-publiques">
      <AccordionTrigger className={ACCORDION_TITLE_CLASS}>
        {t("singleMultipleprintSubtitleCollectionsPubliques")}
      </AccordionTrigger>
      <AccordionContent className="flex flex-col gap-3 font-normal">
        {publicCollectionsFromExemplaires.map((exemplaire) => (
          <div key={exemplaire.id}>
            {exemplaire.collector &&
              computeFieldCollectorName(
                {
                  collectionneursDescriptionNom:
                    exemplaire.collector.collectionneursDescriptionNom ?? "",
                  collectionneursDescriptionPrenom:
                    exemplaire.collector.collectionneursDescriptionPrenom ?? "",
                  collectionneursDescriptionCollectionPublique: true,
                  collectionneursDescriptionVille:
                    exemplaire.collector.collectionneursDescriptionVille,
                  collectionneursDescriptionVillePublished:
                    exemplaire.collector.collectionneursDescriptionVillePublished,
                  collectionneursDescriptionPays:
                    exemplaire.collector.collectionneursDescriptionPays,
                  collectionneursDescriptionPaysPublished:
                    exemplaire.collector.collectionneursDescriptionPaysPublished,
                  showCity: true,
                },
                { language: locale, translation }
              )}
          </div>
        ))}
      </AccordionContent>
    </AccordionItem>
  );
}
